import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import PanelSection from '../common/PanelSection'
import {
  outreachChecklistsApi,
  contactsApi,
  OutreachChecklist as ChecklistType,
  Contact,
} from '../../services/api'
import { useNotification } from '../../hooks/useNotification'
import { PRE_OUTREACH_STAGE } from '../../constants/networking'

interface OutreachChecklistProps {
  contact: Contact
  /**
   * The contact’s outreach status, or undefined when nobody has reached out
   * yet. An unactioned contact gets the pre-outreach checklist.
   */
  contactStatus?: string
  contactId: string
  clientId: string
  onUpdate: (updated: Contact) => void
}

export default function OutreachChecklist({
  contact,
  contactStatus,
  contactId,
  clientId,
  onUpdate,
}: OutreachChecklistProps) {
  const [checklist, setChecklist] = useState<ChecklistType | null>(null)
  const [loading, setLoading] = useState(true)
  // Progress is keyed by item text (not array index) so reorders/inserts/deletes of
  // items by an admin don't silently misalign saved progress. See DEFECT 1213986798255599.
  const [checked, setChecked] = useState<Record<string, boolean>>({})
  const { showError } = useNotification()

  const stage = contactStatus || PRE_OUTREACH_STAGE

  // Reflect the server-persisted progress for the current stage whenever the contact
  // or stage changes. Source of truth is contact.checklistProgress on the Contact record.
  useEffect(() => {
    setChecked(contact.checklistProgress?.[stage] ?? {})
  }, [contact.checklistProgress, stage])

  // Load the checklist template (the list of items to render) for the current stage.
  // This is admin-managed and separate from per-contact progress.
  useEffect(() => {
    const fetchChecklist = async () => {
      setLoading(true)
      try {
        const result = await outreachChecklistsApi.get(stage, clientId)
        setChecklist(result)
      } catch (error) {
        console.error('Failed to load checklist:', error)
        setChecklist(null)
      }
      setLoading(false)
    }
    fetchChecklist()
  }, [stage, clientId])

  const handleToggle = async (itemText: string) => {
    const previousChecked = checked
    const nextStageProgress = { ...checked, [itemText]: !checked[itemText] }

    // Optimistic UI update — flip the box immediately.
    setChecked(nextStageProgress)

    // Build the full checklistProgress object, merging in the updated stage.
    const nextChecklistProgress = {
      ...(contact.checklistProgress ?? {}),
      [stage]: nextStageProgress,
    }

    try {
      const updated = await contactsApi.update({
        id: contactId,
        checklistProgress: nextChecklistProgress,
      } as Contact & { id: string })
      // Propagate the server's canonical copy back to the parent so its contact
      // state stays in sync (important if other fields changed server-side).
      onUpdate(updated)
    } catch (error) {
      console.error('Failed to save checklist progress:', error)
      // Revert the optimistic change so the UI matches what's on the server.
      setChecked(previousChecked)
      showError('Failed to save checklist progress')
    }
  }

  // Deliberately no loading skeleton. A skeleton promises something is coming,
  // and here nothing may be: a stage with no configured checklist renders
  // nothing at all. Holding space for a section that then vanishes is a worse
  // jump than the section simply appearing when it exists.
  if (loading) return null

  if (!checklist || !checklist.items || checklist.items.length === 0) {
    return null
  }

  const completedCount = checklist.items.filter((item) => !!checked[item]).length
  const totalCount = checklist.items.length
  const percent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0

  return (
    <PanelSection
      title={checklist.title}
      aside={
        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {completedCount}/{totalCount}
        </Typography>
      }
    >
      {/* How far through this is, without reading every row. Paired with the
          count above rather than replacing it: a bar alone cannot say "3 of 5",
          and the count alone takes a moment to turn into a sense of progress. */}
      <Box
        role="progressbar"
        aria-valuenow={completedCount}
        aria-valuemin={0}
        aria-valuemax={totalCount}
        aria-label={`${completedCount} of ${totalCount} steps done`}
        sx={{ height: 4, borderRadius: 99, bgcolor: 'action.hover', overflow: 'hidden', mb: 1 }}
      >
        <Box sx={{ height: 4, borderRadius: 99, bgcolor: 'primary.main', width: `${percent}%` }} />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {checklist.items.map((item, index) => (
          <FormControlLabel
            key={`${index}-${item}`}
            control={
              <Checkbox
                size="small"
                checked={!!checked[item]}
                onChange={() => handleToggle(item)}
              />
            }
            label={
              <Typography
                variant="body2"
                sx={{
                  // Strikethrough, not colour alone — this renders in the client
                  // portal too, and "done" has to survive a colourblind reader.
                  textDecoration: checked[item] ? 'line-through' : 'none',
                  color: checked[item] ? 'text.secondary' : 'text.primary',
                }}
              >
                {item}
              </Typography>
            }
            // A checklist row is a tap target on a phone, and MUI's small
            // checkbox leaves it around 34px. The row, not just the box, is
            // what a thumb aims at.
            sx={{ ml: 0, mr: 0, minHeight: 44, alignItems: 'center' }}
          />
        ))}
      </Box>
    </PanelSection>
  )
}
